import {
  booleanAttribute,
  Component,
  input,
  model,
  numberAttribute,
  ViewEncapsulation,
} from '@angular/core';

import { generateId } from '@lc/util';
import { BspkListItem } from '../list-item';
import { BspkTxt } from '../txt';
import { BspkCheckbox } from '../checkbox';

export type MenuItem<TValue = string> = {
  label: string;
  value: TValue;
  id?: string;
  disabled?: boolean;
};

export const MIN_ITEM_COUNT = 2;
export const MAX_ITEM_COUNT = 10;

export const menuDefaults = {
  itemCount: 5,
};

const clampAttribute = (min: number, max: number) => (value: number) => {
  return Math.max(min, Math.min(max, value));
};

@Component({
  selector: 'bspk-menu',
  imports: [BspkListItem, BspkTxt, BspkCheckbox],
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss',
  host: {
    '[attr.data-bspk]': '"menu"',
    role: 'listbox',
    '[attr.data-disabled]': 'disabled() || null',
    '[attr.data-item-count]': 'itemCount()',
    '[attr.data-no-items]': 'items().length === 0 || null',
    '[attr.data-floating]': 'floating() || null',
    '[id]': 'menuId',
    '[style]': '{ "--item-count": itemCount() }',
  },
  encapsulation: ViewEncapsulation.None,
})
export class BspkMenu<TValue = string> {
  readonly selectedValues = model<TValue[]>([]);
  readonly items = input<MenuItem<TValue>[]>([]);
  /**
   * Determines how many items to display at a time
   * minimum: 2
   * maximum: 10
   * @default 5
   */
  readonly itemCount = input(menuDefaults.itemCount, { transform: clampAttribute(MIN_ITEM_COUNT, MAX_ITEM_COUNT) });
  readonly noResultsMessage = input<string>();
  readonly activeIndex = input(null, { transform: numberAttribute });
  readonly isMulti = input(false, { transform: booleanAttribute });
  readonly showSelectionControl = input(true, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly floating = input(false, { transform: booleanAttribute });

  protected readonly menuId = generateId('menu');

  selectValue(item: MenuItem<TValue>, selected: boolean) {
    if (this.disabled() || item.disabled) {
      return;
    }
    this.selectedValues.update((currentSelection) => {
      if (this.isMulti()) {
        if (!selected) {
          return currentSelection.filter((v) => v !== item.value);
        }

        return [...currentSelection, item.value];
      } else {
        return selected ? [item.value] : [];
      }
    });
  }

  getItemId(item: MenuItem<TValue>, index: number) {
    return item.id ?? `${this.menuId}-item-${index}`;
  }
}
